import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router';
import { GraduationCap } from 'lucide-react';
import { useTenant } from '../tenant/TenantProvider.tsx';
import { useTranslation } from 'react-i18next';
import { Header } from './Header.tsx';
import { MobileBottomBar } from './MobileBottomBar.tsx';
import { Sidebar } from './Sidebar.tsx';
import { RouteOutletGuard } from './RouteState.tsx';

export function AppShell() {
  const { t } = useTranslation();
  const { context } = useTenant();
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();
  // Le contenu défile dans <main>, pas dans la fenêtre : chaque nouvel écran commence à son titre.
  // Un changement de filtre (query string) ne déplace pas la page en cours de consultation.
  useLayoutEffect(() => { if (mainRef.current) mainRef.current.scrollTop = 0; }, [pathname]);
  const close = useCallback(() => setMobileOpen(false), []);
  return (
    <div className="flex h-full">
      <a href="#main-content" className="sr-only fixed left-4 top-4 z-[100] rounded-lg bg-white px-4 py-3 text-brand shadow-lg focus:not-sr-only">{t('workspace.skip')}</a>
      <Sidebar mobileOpen={mobileOpen} onClose={close} menuButtonRef={menuButtonRef} />
      <div className="flex min-w-0 flex-1 flex-col">
        <Header onOpenMenu={() => setMobileOpen(true)} menuButtonRef={menuButtonRef} />
        <main ref={mainRef} id="main-content" tabIndex={-1} className="min-w-0 flex-1 overflow-y-auto px-4 pb-24 pt-6 outline-none sm:px-6 md:pb-8 xl:px-8 xl:pt-8">
          <div className="mx-auto max-w-[1440px]">
            {context.tenant.is_training && <div className="mb-5 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-950" data-testid="training-banner">
              <span className="flex items-center gap-2"><GraduationCap size={18} aria-hidden /><strong>{t('training.demo')}</strong><span className="hidden sm:inline">· {t('training.fictitious')}</span></span>
              <Link to="/formation" className="font-semibold underline underline-offset-4">{t('training.open')}</Link>
            </div>}
            <RouteOutletGuard><Outlet /></RouteOutletGuard>
          </div>
        </main>
      </div>
      <MobileBottomBar />
    </div>
  );
}
